import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button.jsx';
import { Input } from '../components/ui/input.jsx';
import { Label } from '../components/ui/label.jsx';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import SocialLoginButtons from '../components/SocialLoginButtons';
import EmailVerificationModal from '../components/EmailVerificationModal';
import { signUpWithEmailAndSendVerification, firebaseSignOut } from '../lib/firebase';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map common typo'd domains → the corrected domain.
// Suggestion is shown only on /register; clicking it auto-corrects the field.
const EMAIL_DOMAIN_TYPOS = {
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmail.con': 'gmail.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'hotmial.com': 'hotmail.com',
  'outloook.com': 'outlook.com',
};

const ROLE_BASED_LOCAL_PARTS = new Set([
  'admin', 'noreply', 'no-reply', 'info', 'support',
  'postmaster', 'webmaster', 'contact',
]);

// Returns the corrected email, or null if no typo detected.
const suggestEmailCorrection = (email) => {
  if (!email || !email.includes('@')) return null;
  const [local, domain] = email.split('@');
  const fix = EMAIL_DOMAIN_TYPOS[(domain || '').toLowerCase()];
  if (!fix) return null;
  return `${local}@${fix}`;
};

const isRoleBasedEmail = (email) => {
  if (!email || !email.includes('@')) return false;
  const local = email.split('@', 1)[0].trim().toLowerCase();
  return ROLE_BASED_LOCAL_PARTS.has(local);
};

const Auth = () => {
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verifyModal, setVerifyModal] = useState({ open: false, email: '', name: '' });
  // Signup-only inline hints: typo suggestion + role-based block + duplicate hint
  const [emailHint, setEmailHint] = useState({
    suggestion: '',     // e.g. "sanjay@gmail.com" (replacement value)
    roleBased: false,
    duplicate: false,
    checking: false,
  });
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get('next');
  const safeNext = (nextPath && nextPath.startsWith('/')) ? nextPath : null;

  const isLogin = mode === 'login';
  const isRegister = mode === 'register';
  const isForgot = mode === 'forgot';

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Signup-form-only client-side gates (defense in depth — backend re-checks)
    if (isRegister) {
      if (isRoleBasedEmail(formData.email)) {
        toast.error('Please use a personal email address.');
        return;
      }
      if (emailHint.duplicate) {
        toast.error('This email is already registered. Sign in instead.');
        return;
      }
    }
    setLoading(true);

    try {
      if (isForgot) {
        const res = await axios.post(`${API}/auth/forgot-password`, { email: formData.email });
        toast.success(res.data?.message || 'Check your inbox.');
        setForgotSent(true);
      } else if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
        navigate(safeNext || '/dashboard');
      } else {
        // Email/password REGISTRATION goes through Firebase so we can require
        // email verification before granting access. The MongoDB user row is
        // created server-side once the user confirms via /api/auth/firebase.
        await signUpWithEmailAndSendVerification(formData.email.trim(), formData.password);
        setVerifyModal({ open: true, email: formData.email.trim(), name: formData.name.trim() });
      }
    } catch (error) {
      // Firebase errors arrive with `code`; backend errors with response.data.detail.
      const code = error?.code || '';
      const httpStatus = error?.response?.status;
      let message;
      if (httpStatus === 429) {
        message = error?.response?.data?.detail || 'Too many attempts. Please try again in a few minutes.';
      } else if (code === 'auth/email-already-in-use') {
        message = 'An account already exists for this email. Please sign in instead.';
      } else if (code === 'auth/weak-password') {
        message = 'Password too weak. Use at least 6 characters.';
      } else if (code === 'auth/invalid-email') {
        message = 'Please enter a valid email address.';
      } else {
        message = error.response?.data?.detail || error?.message || 'An error occurred';
      }
      toast.error(typeof message === 'string' ? message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSuccess = () => {
    navigate(safeNext || '/dashboard');
  };

  const handleVerified = async () => {
    // Backend already minted the JWT inside the modal's loginWithFirebase call.
    // Clean up Firebase client session — our app JWT is what matters from here on.
    await firebaseSignOut();
    setVerifyModal({ open: false, email: '', name: '' });
    navigate(safeNext || '/dashboard');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Recompute signup-only hints whenever the email field changes
    if (e.target.name === 'email' && isRegister) {
      const v = e.target.value.trim();
      setEmailHint({
        suggestion: suggestEmailCorrection(v) || '',
        roleBased: isRoleBasedEmail(v),
        duplicate: false, // reset until next blur fires
        checking: false,
      });
    }
  };

  const handleApplyEmailSuggestion = () => {
    if (!emailHint.suggestion) return;
    setFormData((prev) => ({ ...prev, email: emailHint.suggestion }));
    setEmailHint((prev) => ({ ...prev, suggestion: '', duplicate: false }));
  };

  // On blur of the email field (signup only): silently ask backend if the
  // address is already registered. Show "This email is already registered."
  // if it is.
  const handleEmailBlur = async () => {
    if (!isRegister) return;
    const v = formData.email.trim();
    if (!v || !v.includes('@') || !v.includes('.')) return;
    if (isRoleBasedEmail(v)) return; // already showing the role-based error
    setEmailHint((prev) => ({ ...prev, checking: true }));
    try {
      const res = await axios.get(`${API}/auth/email-exists`, {
        params: { email: v },
      });
      const exists = !!res?.data?.exists;
      setEmailHint((prev) => ({ ...prev, duplicate: exists, checking: false }));
    } catch (_) {
      // silent — never block signup just because the duplicate-check round-trip failed
      setEmailHint((prev) => ({ ...prev, checking: false }));
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setForgotSent(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-32 px-4 noise-texture">
      <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 via-background to-primary/5" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="bg-card border border-border/60 rounded-2xl p-8 shadow-lg">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-heading font-semibold mb-2">
              {isForgot ? 'Reset your password' : (isLogin ? 'Welcome Back' : 'Begin Your Journey')}
            </h2>
            <p className="text-muted-foreground">
              {isForgot
                ? 'Enter your email and we\'ll send you a link to choose a new password.'
                : (isLogin ? 'Continue your spiritual practice' : 'Create your account to start')}
            </p>
          </div>

          {isForgot && forgotSent ? (
            <div
              className="rounded-lg border border-primary/30 bg-primary/5 p-5 text-sm leading-relaxed text-primary/90"
              data-testid="forgot-sent-message"
            >
              <p className="mb-3">
                If an account exists for <strong>{formData.email || 'that email'}</strong>, a reset link
                is on its way. The link is valid for 24 hours.
              </p>
              <p className="text-primary/70 text-xs">
                Don't see it? Check your spam folder, or try again with a different email.
              </p>
            </div>
          ) : (
            <>
              {!isForgot && (
                <>
                  <SocialLoginButtons
                    onSuccess={handleSocialSuccess}
                    disabled={loading}
                  />
                  <div
                    className="my-6 flex items-center gap-4"
                    data-testid="auth-or-divider"
                    aria-hidden="true"
                  >
                    <div className="flex-1 h-px bg-primary/20" />
                    <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">or</span>
                    <div className="flex-1 h-px bg-primary/20" />
                  </div>
                </>
              )}
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="auth-form">
              {isRegister && (
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    data-testid="name-input"
                    className="rounded-lg"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleEmailBlur}
                  placeholder="your@email.com"
                  data-testid="email-input"
                  className="rounded-lg"
                />
                {isRegister && emailHint.roleBased && (
                  <p
                    className="text-xs text-destructive mt-1"
                    data-testid="email-role-based-error"
                  >
                    Please use a personal email address.
                  </p>
                )}
                {isRegister && !emailHint.roleBased && emailHint.suggestion && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Did you mean{' '}
                    <button
                      type="button"
                      onClick={handleApplyEmailSuggestion}
                      data-testid="email-typo-suggestion"
                      className="text-primary hover:text-primary/80 underline underline-offset-4"
                    >
                      {emailHint.suggestion}
                    </button>
                    ?
                  </p>
                )}
                {isRegister && !emailHint.roleBased && emailHint.duplicate && (
                  <p
                    className="text-xs text-destructive mt-1"
                    data-testid="email-duplicate-error"
                  >
                    This email is already registered.{' '}
                    <button
                      type="button"
                      onClick={() => switchMode('login')}
                      className="text-primary hover:text-primary/80 underline underline-offset-4"
                      data-testid="email-duplicate-signin-link"
                    >
                      Sign in instead
                    </button>
                  </p>
                )}
              </div>

              {!isForgot && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    {isLogin && (
                      <button
                        type="button"
                        onClick={() => switchMode('forgot')}
                        className="text-xs text-primary/80 hover:text-primary underline-offset-4 hover:underline transition-colors"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    data-testid="password-input"
                    className="rounded-lg pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/60 hover:text-primary transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="password-toggle-visibility"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-full py-6"
                data-testid="auth-submit-button"
              >
                {loading
                  ? 'Please wait...'
                  : isForgot
                    ? 'Send reset link'
                    : (isLogin ? 'Sign In' : 'Create Account')}
              </Button>
            </form>
            </>
          )}

          <div className="mt-6 text-center space-y-2">
            {isForgot ? (
              <button
                onClick={() => switchMode('login')}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                data-testid="back-to-login"
              >
                ← Back to sign in
              </button>
            ) : (
              <button
                onClick={() => switchMode(isLogin ? 'register' : 'login')}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            )}
          </div>
        </div>
      </motion.div>

      <EmailVerificationModal
        open={verifyModal.open}
        email={verifyModal.email}
        displayName={verifyModal.name}
        onVerified={handleVerified}
      />
    </div>
  );
};

export default Auth;